<template>
  <teleport :to="teleportTarget">
    <transition name="modal" appear>
      <div v-if="show" :class="[{ 'modal-mask': !renderInline }]" @click.self="onOutsideClose">
        <div
          ref="modalRef"
          :class="[{ 'modal-container': !renderInline }, position, type, { marked: marked }]"
          aria-labelledby="modal-header-ref"
          aria-modal="true"
          role="dialog"
          @keyup.esc="$emit('close')"
        >
          <div v-if="$slots.header" :class="['modal__header', { sandBackground: sandColorHeader }]">
            <div id="modal-header-ref" class="header-text">
              <slot name="header" />
            </div>
            <Button
              v-if="!renderInline && showCloseBtn"
              class="exit-button"
              variant="custom"
              type="button"
              :aria-label="textResource('generalText', 'close')"
              @onClick="$emit('close')"
            >
              <Icon name="close" />
            </Button>
          </div>
          <div
            :id="String(modal_content_id)"
            :class="[
              'modal__content',
              state.showShadowBottom ? 'scroll-indicator' : 'scroll-indicator-hide',
            ]"
          >
            <div
              :class="[
                'content-wrapper',
                { noMargin: noMargin },
                { sandBackground: sandColorContent },
              ]"
            >
              <slot name="body" />
            </div>
            <Observer
              :root-id="String(modal_content_id)"
              :offset="24"
              @leaving-viewport-at-bottom="state.showShadowBottom = true"
              @intersected="state.showShadowBottom = false"
            />
          </div>
          <div v-if="showFooter" :class="['modal__footer', { sandBackground: sandColorFooter }]">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts" setup>
import { reactive, PropType, getCurrentInstance, watch, ref } from 'vue'
import textResource from '@/mixins/sharedFunctions/textResource'
import { nextTick } from 'process'
import useFocusTrap from '@/mixins/useFocusTrap'
import Observer from './Observer.vue'
import Icon from './Icon.vue'
import Button from './Button.vue'

const props = defineProps({
  focusFirstInteractable: {
    type: Boolean,
    required: false,
    default: false,
  },
  position: {
    type: String as PropType<'top' | 'bottom' | ''>,
    required: false,
    default: 'bottom',
  },
  type: {
    type: String as PropType<'msg' | 'dialog-in-large' | 'semi-full' | 'full'>,
    required: false,
  },
  marked: {
    type: Boolean,
    required: false,
  },
  renderInline: {
    type: Boolean,
    required: false,
    default: false,
  },
  show: {
    type: Boolean,
    required: false,
    default: false,
  },
  showCloseBtn: {
    type: Boolean,
    required: false,
    default: true,
  },
  outsideClose: {
    type: Boolean,
    required: false,
    default: true,
  },
  noMargin: {
    type: Boolean,
    required: false,
    default: false,
  },
  sandColorHeader: {
    type: Boolean,
    required: false,
    default: true,
  },
  sandColorContent: {
    type: Boolean,
    required: false,
    default: true,
  },
  sandColorFooter: {
    type: Boolean,
    required: false,
    default: true,
  },
  disableTabTrap: {
    type: Boolean,
    required: false,
    default: false,
  },
  teleportTarget: {
    type: String,
    required: false,
    default: '#app',
  },
  showFooter: {
    type: Boolean,
    required: false,
    default: true,
  },
})
const emit = defineEmits(['close'])

const focusTrapRef = useFocusTrap()
const modalRef = ref<HTMLElement>()
const modal_content_id = getCurrentInstance()?.uid

const state = reactive({ showShadowBottom: true })

function onOutsideClose(): void {
  if (props.outsideClose) {
    emit('close')
  }
}

watch(
  () => props.show,
  (value: boolean) => {
    value
      ? nextTick(() => {
          focusTrapRef.initFocusTrap(modalRef)
          setTimeout(() => {
            if (modalRef.value) {
              modalRef.value.focus()
            }
          }, 200)
        })
      : focusTrapRef.clearFocusTrap()
  }
)
</script>

<style lang="scss" scoped>
@import './src/assets/scss/base';

.modal-mask {
  position: fixed;
  z-index: 1000;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: -webkit-fill-available;
  height: calc(100vh - calc(100vh - 100%));
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-container {
  width: 100vw;
  margin: 0px auto;
  border-radius: var(--size-half);
  background-color: var(--color-bilia-white);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transform: translate(0, 0);
  display: flex;
  flex-direction: column;

  &:not(.full) {
    max-height: 85vh;
  }

  &.full {
    min-height: 85vh;
    height: 100vh;
    height: -webkit-fill-available;
    overflow: hidden;
  }

  &.bottom {
    position: absolute;
    bottom: 0;
  }
  &.top {
    transform: translate(0, 0);
  }
  &.marked {
    border-top: solid 0.25rem var(--color-bilia-primary-blue);
  }
  &.msg {
    background: var(--color-super-soft-blue);
  }
  &.confirm {
    background: var(--color-brand-grey-5p);
  }
  .button {
    font-size: var(--font-n);
  }
}

.button.custom {
  margin-bottom: 0;
  border-radius: 0.5rem;
  z-index: 100;
  .icon {
    margin-right: 0;
  }
  :deep() svg {
    height: 1.2em;
  }
  .close-text {
    font-size: var(--font-l);
    @include underline;
  }
  .icon + .close-text {
    display: none;
  }
}

@include media-large {
  .modal-container {
    max-width: var(--content-grid);
    max-height: 85vh;

    &:not(.dialog-in-large) {
      max-height: 85vh;
      top: 50%;
      left: 50%;
      bottom: auto;
      transform: translate(-50%, -50%);
    }

    &.bottom.dialog-in-large {
      left: 50%;
      transform: translateX(-50%);
      bottom: 50%;
      max-width: 680px;
      transform: translate(-50%, 50%);
      -ms-overflow-style: none;
      &::-webkit-scrollbar {
        display: none;
      }
    }

    &.msg {
      -ms-overflow-style: none;
      &::-webkit-scrollbar {
        display: none;
      }
    }
  }
}

.content-wrapper {
  padding: var(--size-full);
  height: 100%;
  background-color: var(--color-bilia-white);
}

.modal__content {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: var(--color-bilia-white);
}

.scroll-indicator::after {
  z-index: 9999;
  content: '';
  position: sticky;
  bottom: 0;
  width: 100%;
  box-shadow: 0 0 10px 8px rgba(0, 0, 0, 0.2);
  display: block;
  opacity: 1;
  transition: opacity 0.1s ease-in-out;
}

.scroll-indicator-hide::after {
  z-index: 9999;
  content: '';
  position: sticky;
  bottom: 0;
  width: 100%;
  box-shadow: 0 0 10px 8px rgba(0, 0, 0, 0.2);
  display: block;
  opacity: 0;
  transition: opacity 0.1s ease-in-out;
}

.noMargin {
  margin: 0;
  padding: 0;
}

.modal__header {
  position: sticky;
  padding-left: var(--size-full);
  padding-right: 0;

  display: flex;
  align-items: center;

  justify-content: space-between;
  background: var(--color-bilia-white);
  z-index: 1;
  border-bottom: 1px solid var(--color-super-soft-blue);
  border-top-left-radius: var(--size-quarter);
  border-top-right-radius: var(--size-quarter);
  .header-text {
    overflow: hidden;

    :deep(span.h3) {
      margin-bottom: 0;
    }
  }

  :deep(h1) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &:empty {
    display: none;
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: background-color 0.3s ease-out;
}

.modal-enter-from,
.modal-leave-to {
  background-color: rgba(0, 0, 0, 0);
}

.modal-enter-to,
.modal-leave-from {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transition: transform 0.3s ease-out;
  &.top {
    transform: translate(0, -100%);
  }
  &.bottom {
    transform: translate(0, 100%);
    @include media-large {
      left: 50%;
      transform: translate(-50%, 100%);
      &.dialog-in-large {
        bottom: 50%;
        transform: translate(-50%, 50%);
      }
    }
  }
}

.modal-enter-to .modal-container,
.modal-leave-from .modal-container {
  transform: translate(0, 0);
  transition: transform 0.3s ease-out;
  @include media-large {
    &.top {
      transform: translate(0, 0);
    }

    &.bottom {
      transform: translate(-50%, -50%);
    }
    &.bottom.dialog-in-large {
      left: 50%;
      transform: translate(-50%, 0);
      bottom: 50%;
      transform: translate(-50%, 50%);
    }
  }
}

.modal__footer {
  padding: var(--size-full);
  background-color: var(--color-bilia-white);
  border-radius: var(--size-half);
  z-index: 1;
  /*   box-shadow: 0px -0.25rem 1rem 0px rgba(0, 0, 0, 0.1); */
  &:empty {
    display: none;
  }
}

.modal__footer .button--wrapper {
  max-width: 50%;
}

.sandBackground {
  background-color: var(--color-bilia-sand-50);
  box-shadow: none;
}

.header-text {
  display: flex;
  flex-direction: row;
  min-height: 3rem;
  align-items: center;
  //column-gap: var(--size-half);
}

.exit-button {
  width: 3rem;
  height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: flex-start;
}

/* hardware acceleration */

* {
  will-change: transform;
  backface-visibility: hidden;
  //  perspective: 1000px;
}
</style>

<style lang="scss">
body.modal-is-open {
  overflow: hidden;
  main {
    overflow: hidden;
  }
}
</style>
